/**
 * A arvore de rotas do SPA (TanStack Router).
 *
 * Dois layouts irmaos sob a raiz, e a diferenca entre eles e a guarda:
 *
 *     raiz
 *     ├── publico       (sem shell)   /entrar, /esqueci-minha-senha, /redefinir-senha
 *     └── aplicacao     (com shell)   /  →  /hoje (o painel do dia, 3.9), as telas de
 *                                      `ITENS_DE_NAVEGACAO`,
 *                                      a ficha da Empresa (/empresas/nova, /empresas/$id),
 *                                      a listagem (/empresas), a Configuracao
 *                                      (/configuracao, 11.2), o Funil (/funil) e o
 *                                      Gerenciar funis (/funil/gerenciar, 11.2), a ficha do
 *                                      Negocio (/negocios/$negocioId, 3.10), a lista de
 *                                      Originados da SDR (/originados, 3.11), a tela de
 *                                      Tarefas (/tarefas, 4.6), a de Relatorios (/relatorios,
 *                                      7.2), a de WhatsApp (/whatsapp, 8.1) e a de
 *                                      Integracoes (/integracoes, 11.2)
 *
 * O roteador e uma **fabrica**, e nao um objeto de modulo: ele carrega o `QueryClient` no
 * contexto (a guarda consulta a sessao por ele), e um roteador unico compartilhado levaria o
 * cache de um teste para o seguinte. `main.tsx` cria um; cada teste cria o seu, com historico
 * de memoria.
 */

import type { QueryClient } from '@tanstack/react-query'
import { type RouterHistory, createRouter } from '@tanstack/react-router'

import { rotaDaAplicacao } from './aplicacao'
import { rotaEmpresaEdicao, rotaEmpresaNova } from './empresa'
import { rotaEmpresas } from './empresas'
import { rotaEntrar } from './entrar'
import { rotaEsqueciMinhaSenha } from './esqueci-minha-senha'
import { rotaFunil } from './funil'
import { rotaDoGrupo } from './grupo'
import { rotaDeGerenciarFunis } from './gerenciar-funis'
import { rotaDeHoje } from './hoje'
import { rotaInicio } from './inicio'
import { rotaDeIntegracoes } from './integracoes'
import { rotaDoNegocio } from './negocio'
import { rotaDeAguardandoEntrega } from './aguardando-entrega'
import { rotaDeConfiguracao } from './configuracao'
import { rotaDeOriginados } from './originados'
import { rotaDePessoas } from './pessoas'
import { rotaPublica } from './publico'
import { rotaRaiz } from './raiz'
import { rotaRedefinirSenha } from './redefinir-senha'
import { rotaDeRelatorios } from './relatorios'
import { rotaDeTarefas } from './tarefas'
import { rotasDasTelas } from './telas'
import { rotaDeWhatsapp } from './whatsapp'

const arvoreDeRotas = rotaRaiz.addChildren([
  rotaPublica.addChildren([rotaEntrar, rotaEsqueciMinhaSenha, rotaRedefinirSenha]),
  // `rotaEmpresaNova` antes de `rotaEmpresaEdicao` por clareza; quem desempata é o roteador,
  // que prefere o segmento estático (`nova`) ao parâmetro (`$empresaId`).
  rotaDaAplicacao.addChildren([
    rotaInicio,
    ...rotasDasTelas,
    rotaEmpresaNova,
    rotaEmpresaEdicao,
    // As duas que sairam do laco de `telas.ts` quando deixaram de ser placeholder — a
    // Administracao na 2.3 e a listagem de Empresas na 2.6. O caminho continua vindo de
    // `CAMINHOS`; o que mudou de casa foi so o corpo da tela.
    rotaDeConfiguracao,
    rotaDeIntegracoes,
    rotaEmpresas,
    // A terceira a sair do laço, na 3.2: o seletor de Funil e o formulário de Negócio.
    rotaFunil,
    rotaDeGerenciarFunis,
    // A quarta, na 3.9: o painel do dia. Ela é o destino de `rotaInicio` e o do login, e por isso
    // precisa vir **da mesma** `CAMINHOS.hoje` que o laço de placeholders deixou de registrar —
    // duas rotas com o mesmo caminho na árvore fariam a escolha ser detalhe de ordem de declaração.
    rotaDeHoje,
    // A quinta, na 3.10: a ficha do Negocio. Ela **nao** vem do laco de `telas.ts` nem de
    // `CAMINHOS` — nao e item de navegacao, e o caminho leva um parametro (`$negocioId`). Como a
    // ficha da Empresa, ela e alcancada por dentro: o card do quadro e a linha do painel do dia.
    rotaDoNegocio,
    // A sexta, na 3.11: a lista de Originados da SDR. Ela **nasce** fora do laço de `telas.ts` —
    // `/originados` é caminho novo em `CAMINHOS`, e não um placeholder que virou tela —, e por isso
    // entra em `COM_TELA_PROPRIA` junto: sem isso, a árvore teria duas rotas para o mesmo caminho.
    rotaDeOriginados,
    rotaDeAguardandoEntrega,
    // A sétima, na 4.6: a tela de Tarefas. Ela **era** placeholder (`/tarefas` está em `CAMINHOS`
    // desde a 1.4), e por isso o caminho entra em `COM_TELA_PROPRIA` no mesmo commit — sem isso, o
    // laço de `telas.ts` registraria uma segunda rota para o mesmo caminho.
    rotaDeTarefas,
    // A oitava, na 7.2: a tela de Relatórios. Ela **era** placeholder (`/relatorios` está em
    // `CAMINHOS` desde a 1.4, como a promessa do Épico 7), e por isso o caminho entra em
    // `COM_TELA_PROPRIA` no mesmo commit — sem isso, o laço de `telas.ts` registraria uma segunda
    // rota para o mesmo caminho.
    rotaDeRelatorios,
    // A nona, na 8.1: a tela de WhatsApp. Ela **era** placeholder (`/whatsapp` está em `CAMINHOS`
    // desde a 1.4, como a promessa do Épico 8), e por isso o caminho entra em `COM_TELA_PROPRIA` no
    // mesmo commit — sem isso, o laço de `telas.ts` registraria uma segunda rota para o mesmo
    // caminho. Com ela, o laço passou a não fabricar nenhuma: era o último placeholder.
    rotaDeWhatsapp,
    // A décima, na 13.4: a tela Pessoas. Ela **nasce** fora do laço, como `/originados` — o caminho
    // é novo em `CAMINHOS` —, e entra em `COM_TELA_PROPRIA` junto.
    rotaDePessoas,
    // A décima primeira, na 13.9: a ficha do Grupo de Empresas. Como a do Negócio, ela **não** é
    // item de navegação nem entra em `CAMINHOS` — o caminho leva um parâmetro (`$grupoId`) e é
    // alcançado pelo cartão Grupo da ficha da Empresa.
    rotaDoGrupo,
  ]),
])

/** Um roteador novo, ligado a este cliente de consultas. */
export function criarRoteador(clienteDeConsultas: QueryClient, historico?: RouterHistory) {
  return createRouter({
    routeTree: arvoreDeRotas,
    defaultPreload: 'intent',
    context: { clienteDeConsultas },
    ...(historico === undefined ? {} : { history: historico }),
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof criarRoteador>
  }
}
